import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DatePicker } from '@mui/x-date-pickers';
import { doc, setDoc } from 'firebase/firestore';
import moment, { Moment } from 'moment';
import { FC, useEffect, useState, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getExperienceYears } from '../../../common/utils';
import { UserExperience, UserStatus } from '../../../consts/types/user';
import { db } from '../../../firebase';
import { UserSelectors, userActionsCreators } from '../../../reducers/user';
const { NotificationManager } = require('react-notifications');

interface AddExperienceDialogProps {
    setIsAddingExperience: (isAdding: boolean) => void;
    setIsSavingExperience: (isSaving: boolean) => void;
    isAddingExperience: boolean;
    isSavingExperience: boolean;
    experienceToEdit?: UserExperience;
}

type Props = AddExperienceDialogProps;


const AddExperienceDialog: FC<Props> = (props: Props) => {
    const { experienceToEdit, isAddingExperience, isSavingExperience, setIsAddingExperience, setIsSavingExperience } = props;
    const user = useSelector(UserSelectors.currentUser);
    const dispatch = useDispatch();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [experience, setExperience] = useState(user?.experience || []);
    const [newCompany, setNewCompany] = useState(experienceToEdit?.companyName || "");
    const [newRole, setNewRole] = useState(experienceToEdit?.role || "");
    // const [newFromYear, setNewFromYear] = useState(experienceToEdit?.fromYear || "");
    const [fromDate, setFromDate] = useState<Moment | null>(experienceToEdit ? moment(new Date(experienceToEdit.fromYear, 0, 1)) : null);
    const [toDate, setToDate] = useState<Moment | null>(experienceToEdit ? experienceToEdit.toYear > -1 ? moment(new Date(experienceToEdit.toYear, 0, 1)) : moment() : null);
    const [currentJob, setCurrentJob] = useState(experienceToEdit?.toYear === -1 || false);
    // const [newToYear, setNewToYear] = useState(experienceToEdit?.toYear || "");
    const [experienceDocument, setExperienceDocument] = useState<File | null>(null);
    const [startup, setStartup] = useState(experienceToEdit?.startup || false);
    const [management, setManagement] = useState(experienceToEdit?.management || false);

    useEffect(() => {
        setExperience(user?.experience || []);
    }, [user?.experience]);

    useEffect(() => {
        if (experienceToEdit) {
            setNewCompany(experienceToEdit.companyName);
            setFromDate(moment(new Date(experienceToEdit.fromYear, 0, 1)));
            if (experienceToEdit.toYear === -1) {
                setToDate(null);
                setCurrentJob(true);
            }
            else {
                setToDate(moment(new Date(experienceToEdit.toYear, 0, 1)));
                setCurrentJob(false);
            }
            setNewRole(experienceToEdit.role);
            // setExperienceDocument(null);
            setStartup(experienceToEdit.startup);
            setManagement(experienceToEdit.management);
        }
    }, [experienceToEdit]);

    const clearExperience = () => {
        setNewCompany("");
        setFromDate(null);
        setToDate(null);
        setExperienceDocument(null);
        setNewRole("");
        setStartup(false);
        setManagement(false);
        setCurrentJob(false);
    }

    const addNewExperience = () => {
        if (user?.email && newCompany && fromDate && fromDate.isValid() && newRole) {
            const newExperience = {
                companyName: newCompany,
                role: newRole,
                startup,
                management,
                fromYear: fromDate.year(),
                toYear: toDate && toDate.isValid() && !currentJob ? toDate.year() : -1,
                document: experienceDocument ? experienceDocument.name : null,
                status: experienceDocument ? UserStatus.Pending : UserStatus.NotVerified
            }
            setIsAddingExperience(false);
            setIsSavingExperience(true);

            if (user) {
                dispatch(userActionsCreators.setUser({ user: { ...user, experience: [...experience, newExperience] } }));
            }
            const experienceYears = getExperienceYears([...experience, newExperience]);
            setDoc(doc(db, "users", user.email), {
                experience: [...experience, newExperience],
                experienceYears,
                startup: [...experience, newExperience].some(e => !!e.startup),
                management: [...experience, newExperience].some(e => !!e.management),
            }, { merge: true })
                .then((result) => {

                    NotificationManager.success('Experience added successfully!',);
                    // upload file
                    // if (experienceDocument) {
                    //     const storageRef = ref(storage, `${user.email}/${newCompany}__${experienceDocument.name}`);
                    //     uploadBytes(storageRef, experienceDocument);
                    // }
                    setIsSavingExperience(false);
                })
                .catch(() => {
                    setIsSavingExperience(false);
                    NotificationManager.error('Something went wrong. Please try again.',);
                });
            clearExperience();
        }
    }

    return <Dialog fullScreen={fullScreen} onClose={() => { setIsAddingExperience(false) }} open={isAddingExperience}>
        <DialogTitle>Add Experience</DialogTitle>
        <Box px={2}>
            <Stack gap={3}>
                <TextField size="small" value={newCompany} onChange={e => setNewCompany(e.target.value)} label="Company" fullWidth required />
                <TextField size="small" value={newRole} onChange={e => setNewRole(e.target.value)} label="Role" fullWidth required />
                <Stack direction="row" spacing={2}>
                    <DatePicker label={'From Year'} views={['year']}
                        value={fromDate}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth required />}
                        onChange={newValue => { setFromDate(newValue) }} />
                    <DatePicker label={'To Year'} views={['year']} disabled={currentJob}
                        value={toDate}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                        onChange={newValue => { setToDate(newValue) }} />
                    <FormControlLabel control={<Checkbox checked={currentJob} onChange={(e, checked) => { setCurrentJob(checked) }} />}
                        label={<Box display="flex" flexDirection="column">
                            <Typography >Current</Typography>
                            {/* {largeCompanies && <Typography variant="caption" >{largeCompaniesDocument?.name || user?.largeCompaniesDocument}</Typography>} */}
                        </Box>} />
                    {/* <TextField size="small" type="number" value={newFromYear} onChange={e => setNewFromYear(e.target.value)} label="From Year" fullWidth required /> */}
                    {/* <TextField size="small" type="number" value={newToYear} onChange={e => setNewToYear(e.target.value)} label="To Year" fullWidth /> */}
                </Stack>
                <Box display="flex" >
                    <FormControlLabel control={<Checkbox checked={startup} onChange={(e, checked) => { setStartup(checked) }} />}
                        label={<Box display="flex" flexDirection="column">
                            <Typography>Startup Company</Typography>
                            {/* {largeCompanies && <Typography variant="caption" >{largeCompaniesDocument?.name || user?.largeCompaniesDocument}</Typography>} */}
                        </Box>} />
                </Box>
                <Box display="flex" mt={-2}>
                    <FormControlLabel control={<Checkbox checked={management} onChange={(e, checked) => { setManagement(checked) }} />}
                        label={<Box display="flex" flexDirection="column">
                            <Typography>Management Experience</Typography>
                            {/* {largeCompanies && <Typography variant="caption" >{largeCompaniesDocument?.name || user?.largeCompaniesDocument}</Typography>} */}
                        </Box>} />
                </Box>
                {/* <Button variant="outlined" component="label" >
                <AttachFile />
                Verifying Document
                <input hidden
                    onChange={e => { setExperienceDocument(e.target.files ? e.target.files[0] : null) }}
                    accept=".doc,.docx,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    multiple type="file" />
            </Button> */}
                {experienceDocument && <Typography fontSize={{ xs: "0.75rem", md: "0.875rem" }} color="GrayText">{experienceDocument.name}</Typography>}
                <Box display="flex" justifyContent="center" my={2}>
                    <Button onClick={() => { clearExperience(); setIsAddingExperience(false); }} color="inherit" sx={{ mr: 2 }}>Cancel</Button>
                    <Button variant="contained" onClick={addNewExperience} disabled={isSavingExperience}>Submit</Button>
                </Box>
            </Stack>
        </Box>
    </Dialog>;
}


export default AddExperienceDialog;

